import db from "../db/database";
import { sendToBusinessCentral } from "../utils/businessCentralUtils";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import { getClientByNoOrId } from "./clientService";
import { syncClients } from "./clientSyncService";
import { getInvoicePaymentLines } from "./invoicePaymentService";
import { getInvoiceLines } from "./invoiceService";

// Función para transformar las facturas al formato esperado por Business Central
export const transformInvoiceForBC = async (invoice, lines) => {
	try {
		// Obtener el tipo de factura (para poder construir correctamente la estructura)
		const isInvoice = invoice.Document_Type === "Invoice";
		const isCreditMemo = invoice.Document_Type === "Credit Memo";

		const client = await db.clients
			.where("No")
			.equals(invoice.Sell_to_Customer_No_)
			.or("id")
			.equals(invoice.Sell_to_Customer_No_)
			.first();

		// Obtener las líneas de pago para calcular el descuento máximo
		const paymentLines = await getInvoicePaymentLines(invoice.id);

		// Obtener el descuento máximo de las formas de pago
		let maxDiscount = 0;
		if (paymentLines && paymentLines.length > 0) {
			maxDiscount = paymentLines.reduce(
				(max, line) => ((line.Dto || 0) > max ? line.Dto || 0 : max),
				0
			);
		}

		// Calcular el importe del descuento
		// Primero calculamos el total de la factura
		let subtotal = 0;
		let vat = 0;

		lines.forEach((line) => {
			const quantity = line.Quantity || 0;
			const unitPrice = line.Unit_Price || 0;
			const discount = line.Line_Discount_ || 0;
			const discountAmount = unitPrice * (discount / 100);
			const finalPrice = unitPrice - discountAmount;
			const lineSubtotal = quantity * finalPrice;

			// Aplicar IVA específico del producto
			const lineIva = line.PorIva || 0;
			const lineVat = lineSubtotal * (lineIva / 100);

			subtotal += lineSubtotal;
			vat += lineVat;
		});

		const total = subtotal + vat;

		// Calcular el importe del descuento
		const importeDescuento = maxDiscount > 0 ? total * (maxDiscount / 100) : 0;

		// Preparar cabecera de la factura
		const customerNo = client?.No || (typeof invoice.Sell_to_Customer_No_ === 'string' ? invoice.Sell_to_Customer_No_ : undefined);
		const header = {
			Document_Type: invoice.Document_Type,
			Posting_Date: invoice.Posting_Date,
			Document_Date: invoice.Document_Date,
			// Forzar a usar No del cliente; si no existe, dejaremos que la validación superior bloquee
			Sell_to_Customer_No_: customerNo,
			Bill_to_Customer_No_: customerNo,
			// Datos de facturación completos
			Bill_to_Name: invoice.Bill_to_Name,
			Bill_to_Address: invoice.Bill_to_Address,
			Bill_to_City: invoice.Bill_to_City || "",
			VAT_Registration_No_: invoice.VAT_Registration_No_ || "",
			Bill_to_Country_Region_Code: invoice.Bill_to_Country_Region_Code || "",
			// Código de dirección de envío
			Ship_to_Code: invoice.Ship_to_Code || "01",
			// Tipo de documento de identificación fiscal
			Tipo_Documento_SrI: invoice.Tipo_Documento_SrI || "Cedula",
			// Datos de envío (usamos los mismos que de facturación)
			Sell_to_Name: invoice.Bill_to_Name,
			Sell_to_Address: invoice.Bill_to_Address,
			Sell_to_City: invoice.Bill_to_City || "",
			Colegio: invoice.Colegio || "",
			Caja: invoice.Caja || invoice.caja || "",
			Turno: invoice.Turno || invoice.turno || "",
			// Incluir campos de descuento
			Dto: maxDiscount,
			importeDto: importeDescuento.toFixed(2),
			// Incluir campos de cupón
			Cupon: invoice.Cupon || "",
			Tipo_Detalle: invoice.Tipo_Detalle || "",
			No_Detalle: invoice.No_Detalle || "",
			// Incluir campo de campaña
			Campaign_No_: invoice.Campaign_No_ || "",
		};

		// Si es un abono, añadir la referencia a la factura original
		if (isCreditMemo) {
			header.Applies_to_Doc_Type = "Invoice";
			header.Applies_to_Doc_No = invoice.Applies_to_Doc_No;
		}
		// Crear la estructura completa de datos para enviar a BC
		// El formato depende del tipo de documento
		let data;
		if (isInvoice) {
			data = header;
		} else if (isCreditMemo) {
			data = header;
		} else {
			throw new Error("Tipo de documento no soportado");
		}

		return data;
	} catch (error) {
		console.error("Error al transformar factura para BC:", error);
		throw error;
	}
};

// Transformar línea de factura para enviarla a Business Central
export const transformInvoiceLineForBC = (invoiceLine, documentNo, index) => {
	// Crear objeto de línea de factura según formato API BC
	const line = {
		Document_Type: invoiceLine.Document_Type || "Invoice",
		Document_No_: documentNo,
		Line_No_: invoiceLine.Line_No || (index + 1) * 10000,
		Type: invoiceLine.Type || "Item",
		No_: invoiceLine.No || "",
		Description: invoiceLine.Description || "",
		Quantity: invoiceLine.Quantity || 0,
		Unit_Price: invoiceLine.Unit_Price || 0,
		Line_Discount_: invoiceLine.Line_Discount_ || 0,
		PorIva: invoiceLine.PorIva || 0,
	};

	// Filtrar campos con valores vacíos
	return Object.fromEntries(
		Object.entries(line).filter(([_, value]) => value !== "")
	);
};

// Transformar línea de pago para enviarla a Business Central
export const transformInvoicePaymentLineForBC = (
	paymentLine,
	documentNo,
	index
) => {
	// Crear objeto de línea de pago según formato API BC
	const line = {
		Document_Type: paymentLine.Document_Type || "Invoice",
		Document_No_: documentNo,
		Line_No: paymentLine.Line_No || (index + 1) * 10000,
		Payment_Method: paymentLine.Payment_Method || "",
		Amount: paymentLine.Amount || 0,
		Dto: paymentLine.Dto || 0,
		Dto_Amount: paymentLine.Dto_Amount || 0,
	};

	// Filtrar campos con valores vacíos
	return Object.fromEntries(
		Object.entries(line).filter(([_, value]) => value !== "")
	);
};

// Sincronizar una factura a Business Central
export const syncInvoiceToBC = async (invoice) => {
	try {

		// Verificar que el cliente está sincronizado y tenemos un No válido
		const client = await getClientByNoOrId(invoice.Sell_to_Customer_No_);
		if (!client || !client.No) {
			// Intentar refrescar clientes por si se sincronizó recientemente
			await syncClients();
			const refreshed = await getClientByNoOrId(invoice.Sell_to_Customer_No_);
			if (!refreshed || !refreshed.No) {
				return {
					success: false,
					pendingClientSync: true,
					clientId: invoice.clientLocalId || invoice.Sell_to_Customer_No_,
					message: "Cliente sin 'No' en BC. Sincronice el cliente antes de enviar la factura.",
				};
			}
		}

		// Obtener las líneas de la factura
		const invoiceLines = await getInvoiceLines(invoice.id);

		// Obtener las líneas de pago
		const paymentLines = await getInvoicePaymentLines(invoice.id);

		// Calcular descuento máximo de formas de pago
		let maxDiscount = 0;
		if (paymentLines && paymentLines.length > 0) {
			maxDiscount = paymentLines.reduce(
				(max, line) => ((line.Dto || 0) > max ? line.Dto || 0 : max),
				0
			);
		}

		// Calcular el total de la factura con IVA específico por producto
		let subtotal = 0;
		let vat = 0;

		invoiceLines.forEach((line) => {
			const quantity = line.Quantity || 0;
			const unitPrice = line.Unit_Price || 0;
			const discount = line.Line_Discount_ || 0;
			const discountAmount = unitPrice * (discount / 100);
			const finalPrice = unitPrice - discountAmount;
			const lineSubtotal = quantity * finalPrice;

			// Aplicar IVA específico del producto
			const lineIva = line.PorIva || 0;
			const lineVat = lineSubtotal * (lineIva / 100);

			subtotal += lineSubtotal;
			vat += lineVat;
		});

		const total = subtotal + vat;

		// Calcular importe de descuento
		const importeDescuento = maxDiscount > 0 ? total * (maxDiscount / 100) : 0;

		// Actualizar los campos Dto e importeDto en la factura
		await db.invoices.update(invoice.id, {
			Dto: maxDiscount,
			importeDto: importeDescuento.toFixed(2),
		});

		// Recargar la factura actualizada
		const updatedInvoice = await db.invoices.get(invoice.id);

		// 1. Preparar la cabecera de la factura para BC
		const invoiceForBC = await transformInvoiceForBC(
			updatedInvoice,
			invoiceLines
		);
		if (!invoiceForBC.Sell_to_Customer_No_) {
			return {
				success: false,
				pendingClientSync: true,
				clientId: invoice.clientLocalId || invoice.Sell_to_Customer_No_,
				message: "Falta 'Sell_to_Customer_No_' válido. Sincronice el cliente.",
			};
		}
		const invoiceData = {
			data: JSON.stringify({ Sales_Headers: [invoiceForBC] }),
		};

		// 2. Enviar la cabecera a Business Central
		const headerResponse = await sendToBusinessCentral(
			"Importaciones_insertaFacturasVenta",
			invoiceData,
			true
		);

		// 3. Obtener número de documento de la respuesta
		const documentNo = headerResponse.value;
		if (!documentNo) {
			throw new Error(
				"No se pudo obtener el número de documento de la factura"
			);
		}

		// 4. Actualizar la factura local con el número asignado por BC
		await db.invoices.update(invoice.id, {
			No: documentNo, // Guardamos el número que nos devuelve BC
			lastSynced: getCurrentDateTimeISO(),
			pendingSync: 0,
		});

		// 6. Preparar las líneas para BC
		const transformedLines = invoiceLines.map((line, index) =>
			transformInvoiceLineForBC(line, documentNo, index)
		);

		// 7. Enviar las líneas a Business Central
		const linesData = {
			data: JSON.stringify({ Sales_Lines: transformedLines }),
		};

		await sendToBusinessCentral(
			"Importaciones_insertaLineasFacturasVenta",
			linesData,
			true
		);

		// 8. Actualizar estado de sincronización de líneas
		for (const line of invoiceLines) {
			await db.invoiceLines.update(line.id, {
				lastSynced: getCurrentDateTimeISO(),
				pendingSync: 0,
			});
		}

		if (paymentLines && paymentLines.length > 0) {
			// 10. Preparar las líneas de pago para BC
			const transformedPaymentLines = paymentLines.map((line, index) =>
				transformInvoicePaymentLineForBC(line, documentNo, index)
			);

			// 11. Enviar las líneas de pago a Business Central
			const paymentLinesData = {
				data: JSON.stringify({ Sales_Payment_Lines: transformedPaymentLines }),
			};

			await sendToBusinessCentral(
				"Importaciones_insertaPagosFacturasVenta",
				paymentLinesData,
				true
			);

			// 12. Actualizar estado de sincronización de líneas de pago
			for (const line of paymentLines) {
				await db.invoicePaymentLines.update(line.id, {
					lastSynced: getCurrentDateTimeISO(),
					pendingSync: 0,
				});
			}
		}

		return { success: true, message: "Factura sincronizada correctamente" };
	} catch (error) {
		console.error("Error al sincronizar factura:", error);
		return { success: false, message: error.message };
	}
};

// Sincronizar todas las facturas pendientes
export const syncInvoices = async () => {
	try {
		// Obtener facturas pendientes de sincronización que NO sean borradores
		const pendingInvoices = await db.invoices
			.where("pendingSync")
			.equals(1)
			.and((invoice) => !invoice.isDraft && !invoice.deleted) // Excluir borradores y facturas eliminadas
			.toArray();

		const results = [];
		const pendingClientSyncInvoices = [];

		// Sincronizar cada factura pendiente
		for (const invoice of pendingInvoices) {
			const result = await syncInvoiceToBC(invoice);

			if (result.pendingClientSync) {
				// La factura tiene un cliente que no está sincronizado
				pendingClientSyncInvoices.push({
					invoiceId: invoice.id,
					clientId: result.clientId,
					message: result.message,
				});
				console.log(
					`Factura ${invoice.id} pendiente de sincronización del cliente ${result.clientId}`
				);
			} else {
				results.push(result);
			}
		}

		// Si hay facturas pendientes de sincronización de clientes, mostrar en consola
		if (pendingClientSyncInvoices.length > 0) {
			console.log(
				"Las siguientes facturas están pendientes de sincronización de clientes:",
				pendingClientSyncInvoices
			);
		}

		return {
			success: true,
			results,
			pendingClientSync: pendingClientSyncInvoices.length > 0,
			pendingClientSyncInvoices,
		};
	} catch (error) {
		console.error("Error al sincronizar facturas:", error);
		return { success: false, message: error.message };
	}
};
